import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import debug from 'debug';

const log = debug('rois:components:tables:machine-selection');

if (import.meta.env.DEV) {
  debug.enable('rois:components:tables:machine-selection*');
}

import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  Row,
  getFilteredRowModel,
} from '@tanstack/react-table';

// Styled Components
import {
  Container,
  SubContainer,
  SubContainerRight,
  ContainerTables,
  ContainerSearchBar,
  Table,
  Thead,
  Th,
  ThRightTable,
  Tr,
  TrBody,
  Td,
  TableRight,
  Tbody,
  TbodyRight,
  Img,
  Input,
  TdRightTable,
  StyledInput,
  FiltersContainer,
  DropDownFilterStyled,
} from './styles';

// Components
import { Typography } from '../../../Typography';
import SearchBar from '../../../../inputs/SearchBar';
import Badge from '../../../Badge';

//Icons
import ArrowIcon from '../../../../../assets/icons/grey-right-arrow-icon.svg';
import RemoveIcon from '../../../../../assets/icons/remove-file-icon.svg';

// Double tap row
import { DoubleTapRow } from '../../components/DoubleTapRow';

// Types
import {
  dataTable,
  tableProps,
  columnNames,
  getColumnId,
  onChangeInputTypes,
  variantThRight,
  variantThLeft,
} from './types';
import { OrderState } from '../../../../../types/orders';
import { Role } from '../../../../../types/auth';
import { useAuth } from '../../../../../hooks/useAuth';
import { OptionSelected } from '../../../../inputs/DropDown/DropDownList/types';

// Mocks
import { mockData } from './mocks';
import { textSearchFilter } from '../../textSearchFilter';

export const MachineSelectionTable = ({
  data,
  teams,
  defaultValues = [],
  setValue,
  register,
  onAdd,
  onDelete,
  onUpdate,
  ...props
}: tableProps): JSX.Element => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedRows, setSelectedRows] = useState<Row<dataTable>[]>([]);
  const [dataTableRight, setDataTableRight] =
    useState<dataTable[]>(defaultValues);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const dataTable = data?.machines ? data.machines : mockData;

  // Clear highlight after 1.5 seconds
  useEffect(() => {
    if (highlightedRowId) {
      const timer = setTimeout(() => setHighlightedRowId(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [highlightedRowId]);

  const columnHelper = createColumnHelper<dataTable>();

  const handleClickRow = useCallback(
    (row: Row<dataTable>) => {
      log('🔧➕ handleClickRow - Añadiendo maquinaria:', row.original);
      log('🔧➕ handleClickRow - row.original.id:', row.original.id);

      const newData: dataTable = {
        auxMachineId: row.original.auxMachineId,
        description: row.original.description,
        typeOfMeasurement: row.original.typeOfMeasurement,
        id: row.original.id,
        isInternal: row.original.isInternal !== undefined ? row.original.isInternal : true,
      };

      log('🔧➕ handleClickRow - newData creado:', newData);

      const id = row.original.id;

      const setValues = () => {
        log('🔧➕ handleClickRow - Seteando valores en form con id:', id);
        setValue(
          `machineryTable[${id}].auxMachineId`,
          row.original.auxMachineId
        );
        setValue(`machineryTable[${id}].description`, row.original.description);
        setValue(
          `machineryTable[${id}].typeOfMeasurement`,
          row.original.typeOfMeasurement
        );
        setValue(`machineryTable[${id}].ut`, '');
        setValue(`machineryTable[${id}].id`, row.original.id);
        setValue(
          `machineryTable[${id}].isInternal`,
          row.original.isInternal !== undefined ? row.original.isInternal : true
        );
      };

      const duplicate =
        selectedRows.some(
          (element) => element.original.id === row.original.id
        ) || dataTableRight.some((element) => element.id === row.original.id);

      log('🔧➕ handleClickRow - ¿Es duplicado?:', duplicate);

      !duplicate && setValues();
      !duplicate && setDataTableRight([...dataTableRight, newData]);
      !duplicate && setSelectedRows([...selectedRows, row]);
      !duplicate && onAdd && onAdd(row.original.id as string);

      log('✅ handleClickRow - Maquinaria añadida a la tabla');
      log('🔧➕ handleClickRow - dataTableRight actualizado:', [...dataTableRight, newData]);
    },
    [dataTableRight, selectedRows]
  );

  const removeRow = useCallback(
    (row: Row<dataTable>) => {
      log('🔧❌ removeRow - Borrando maquinaria:', {
        id: row.original.id,
        auxMachineUnitId: row.original.auxMachineUnitId,
        auxMachineId: row.original.auxMachineId
      });

      setDataTableRight((element) =>
        element.filter((item) => item.id !== row.original.id)
      );

      setSelectedRows((element) =>
        element.filter((item) => item.original.id !== row.original.id)
      );

      setValue(`machineryTable[${row.original.id}]`, undefined);

      log('🔧❌ removeRow - Llamando a onDelete');
      onDelete &&
        onDelete(
          row.original.id as string,
          row.original.auxMachineUnitId as string
        );
      log('✅ removeRow - Maquinaria borrada de la tabla');
    },
    [dataTableRight]
  );

  const columnsLeft = useMemo(
    () => [
      columnHelper.accessor('add' as any, {
        header: () => (
          <Typography colorText="white" size="S">
            +
          </Typography>
        ),
        cell: () => <Img src={ArrowIcon} />,
      }),
      columnHelper.accessor(getColumnId(columnNames.auxMachineId.id), {
        header: () => (
          <Typography size="S" colorText="white">
            {columnNames.auxMachineId.label}
          </Typography>
        ),
        cell: (info) => (
          <Typography size="S">{info.getValue() as string}</Typography>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.description.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.description.label}
          </Typography>
        ),
        cell: (info) => (
          <StyledInput value={info.getValue() as string} readOnly />
        ),
        // Sin acentos y por palabras (antes 'includesString', solo esta
        // columna): el buscador mira también las columnas de texto que se ven.
        filterFn: textSearchFilter((row) => [row.auxMachineId, row.description, row.type]),
      }),
      columnHelper.accessor(getColumnId(columnNames.type.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.type.label}
          </Typography>
        ),
        cell: (info) => (
          <StyledInput value={info.getValue() as string} readOnly />
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.origin.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.origin.label}
          </Typography>
        ),
        cell: (info) => (
          <Badge
            variant={info.getValue() ? 'internal' : 'external'}
            size="small"
          >
            {info.getValue() ? 'Intern' : 'Extern'}
          </Badge>
        ),
      }),
    ],
    [selectedRows]
  );
  const columnsRight = useMemo(
    () => [
      columnHelper.accessor(getColumnId(columnNames.auxMachineId.id), {
        header: () => (
          <Typography size="S" colorText="white">
            {columnNames.auxMachineId.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(
              `machineryTable[${info.row.original.id}].auxMachineId`
            )}
            disabled={true}
            type="string"
          />
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.description.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.description.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(`machineryTable[${info.row.original.id}].description`)}
            disabled={true}
            type="string"
          />
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.origin.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.origin.label}
          </Typography>
        ),
        cell: (info) => (
          <Badge
            variant={info.getValue() ? 'internal' : 'external'}
            size="small"
          >
            {info.getValue() ? 'Intern' : 'Extern'}
          </Badge>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.ut.id), {
        header: () => (
          <Typography size="S" colorText="white">
            {columnNames.ut.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(`machineryTable[${info.row.original.id}].ut`)}
            disabled={
              data?.parentExpeditionOrderStatus === OrderState.Annulled ||
              data?.parentExpeditionOrderStatus === OrderState.End ||
              data?.state === OrderState.Annulled
            }
            type="number"
            onChange={(event) =>
              onUpdate &&
              onUpdate(
                event.target.value,
                info.row.original.id as string,
                info.row.original.auxMachineUnitId as string,
                `machineryTable[${info.row.original.id}].ut` as string
              )
            }
            onKeyDown={(e) => {
              /[+*/.,\\-]/.test(e.key) && e.preventDefault();
            }}
          />
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.typeOfMeasurement.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.typeOfMeasurement.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(
              `machineryTable[${info.row.original.id}].typeOfMeasurement`
            )}
            disabled={true}
            type="string"
          />
        ),
      }),
      columnHelper.accessor('remove' as any, {
        header: () => (
          <Typography colorText="white" size="S">
            -
          </Typography>
        ),
        cell: ({ row }) => (
          <Img
            src={RemoveIcon}
            onClick={() =>
              data?.parentExpeditionOrderStatus !== OrderState.Annulled &&
              data?.parentExpeditionOrderStatus !== OrderState.End &&
              data?.state !== OrderState.Annulled &&
              removeRow(row)
            }
          />
        ),
      }),
    ],
    [dataTableRight]
  );

  const table = useReactTable({
    data: dataTable,
    columns: columnsLeft,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {},
  });

  const tableRight = useReactTable({
    data: dataTableRight,
    columns: columnsRight,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    const allLeftRows = table.getCoreRowModel().rows;
    const similarTables = allLeftRows.filter((row) => {
      const code = row.original.auxMachineId;

      const matchingItem = dataTableRight.find(
        (item) => item.auxMachineId === code
      );

      if (matchingItem?.auxMachineUnitId) {
        row.original.auxMachineUnitId = matchingItem.auxMachineUnitId;
      }

      return !!matchingItem;
    });

    setSelectedRows(similarTables);
  }, []);

  const onChangeInput = useCallback<onChangeInputTypes>((value) => {
    const machinery = table.getColumn(columnNames?.description?.id);
    machinery?.setFilterValue(value);
  }, []);

  return (
    <Container {...props}>
      <FiltersContainer>
        <ContainerSearchBar>
          <SearchBar
            variant="form"
            onChangeInput={(e) => onChangeInput(e.target.value)}
          />
        </ContainerSearchBar>
      </FiltersContainer>
      <ContainerTables>
        <SubContainer>
          <Table>
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th variant={header.id as variantThLeft} key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                const isDisabled =
                  data?.parentExpeditionOrderStatus === OrderState.Annulled ||
                  data?.parentExpeditionOrderStatus === OrderState.End ||
                  data?.state === OrderState.Annulled;

                return (
                  <DoubleTapRow
                    key={row.id}
                    onSingleTap={() => !isDisabled && setHighlightedRowId(row.id)}
                    onDoubleTap={() => !isDisabled && handleClickRow(row)}
                    isDisabled={isDisabled}
                    singleClickOnDesktop={true}
                    StyledComponent={TrBody}
                    componentProps={{ $highlighted: highlightedRowId === row.id }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        variant={cell.id.slice(2) as variantThLeft}
                        key={cell.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                  </DoubleTapRow>
                );
              })}
            </Tbody>
          </Table>
        </SubContainer>
        <SubContainerRight>
          <TableRight>
            <Thead>
              {tableRight.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <ThRightTable
                      variant={header.id as variantThRight}
                      key={header.id}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </ThRightTable>
                  ))}
                </Tr>
              ))}
            </Thead>
            <TbodyRight>
              {tableRight.getRowModel().rows.map((row) => (
                <TrBody key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TdRightTable
                      variant={cell.id.slice(2) as variantThRight}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TdRightTable>
                  ))}
                </TrBody>
              ))}
            </TbodyRight>
          </TableRight>
        </SubContainerRight>
      </ContainerTables>
    </Container>
  );
};
